<script setup>
// The club page's availability card (BOOKINGS_PLAN.md §6, B16's words;
// AppBookClub; X42: headed "Court availability" / "Table availability" by the
// API's availabilityTitle, "Playing area availability" when the areas differ):
// Grant 2026-11-02: one availability bar for the rest of today across every booking rule ("14 of 30 slots free today",
// /book's meter colours) and Book; the per-area tiles, the hours lines and the legend are gone.
// Shown only when the club takes bookings. Reads the anonymous availability for
// today, so it costs one small call on clubs with bookings off (the club
// response doesn't say yet). X45: a player's own bookings left this card for
// the page's "Your bookings" entry and dialog.
import {computed, onMounted, ref} from 'vue';
import {useAuthService} from '~/composables/useAuthService';
import {useBookingsMember} from '~/composables/useBookingsMember';
import {useVisitorPreview} from '~/composables/useVisitorPreview';
import {BOOKING_ICON, takesBookings} from '~/utils/bookingsMember';
import {todayAvailability} from '~/utils/bookingWeek';
import {areaWords} from '~/utils/areaWords';

const props = defineProps({
  clubId: {type: String, required: true},
  /** The visitor preview: the button is shown but not live (the flag itself comes from useVisitorPreview). */
  preview: {type: Boolean, default: false},
  /** X222 (R91 O-1): the page's own copy, asked before it rendered: {availability, at} (at = the server's moment). */
  initial: {type: Object, default: null},
});
const emit = defineEmits(['loaded']);

const api = useBookingsMember();
const {isAuthenticated} = useAuthService();
// X73: in the visitor preview the availability is a visitor's (the request carries X-View-As) and so is the button.
const visitorPreview = useVisitorPreview();
const signedIn = computed(() => isAuthenticated.value && !visitorPreview.value);
const availability = ref(props.initial?.availability ?? null);
// The server's moment first, so the tiles hydrate with the words it drew; the browser's own once mounted.
const now = ref(props.initial?.at ? new Date(props.initial.at) : new Date());

const load = async () => {
  try {
    availability.value = await api.availability(props.clubId, {days: 1});
  } catch {
    availability.value = null;
  }
  now.value = new Date();
  emit('loaded', takesBookings(availability.value));
};
onMounted(() => {
  if (!props.initial?.availability) { load(); return; }
  now.value = new Date();
  emit('loaded', takesBookings(availability.value));
});

// X226: on = the club takes bookings (on by default for every club; something must be bookable).
const on = computed(() => takesBookings(availability.value));
// X42: the heading comes from the API ("Court availability", "Court & board availability"); Grant 2026-11-02: when the
// API says "playing area" the areas' own names are tried first (areaWords), the fallback is the last resort.
const title = computed(() => areaWords(availability.value).title);
// Grant 2026-11-02: one bar for what is left of today across every booking rule (/book's meter), never a tile per area.
const today = computed(() => todayAvailability(availability.value, now.value));
const bookTo = computed(() => `/clubs/${props.clubId}/book`);
// Signed out, the booking page decides (a visitor sheet when the club allows
// visitors, else "Sign in to book"), so the button always reads Book.
const canBook = computed(() => !!availability.value?.viewer?.canBook || !signedIn.value);
</script>

<template>
  <template v-if="on">
    <div class="tl-section-head">
      <h2>{{ title }}</h2>
    </div>
    <!-- Grant 2026-11-02: the line and bar on the left, a short Book button on the right, centred -->
    <div class="tl-card crn-card" data-testid="club-availability">
      <div class="crn-main">
        <div class="crn-line">{{ today.line }}</div>
        <div v-if="today.level !== 'none'" class="crn-bar" :class="'crn-bar--' + today.level" role="img" :aria-label="today.line">
          <span class="crn-fill" :style="{width: Math.round(today.fill * 100) + '%'}"/>
        </div>
      </div>
      <v-btn color="primary" rounded="pill" size="small" :prepend-icon="BOOKING_ICON" :to="bookTo" :disabled="preview">{{ canBook ? 'Book' : 'See the grid' }}</v-btn>
    </div>
  </template>
</template>

<style scoped lang="scss">
.crn-card { padding: 12px 14px; display: flex; align-items: center; gap: 14px; }
.crn-main { flex: 1 1 auto; min-width: 0; }
.crn-line { font-size: 13px; font-weight: 600; color: rgb(var(--v-theme-text)); }
/* Grant 2026-11-02: a subtle outline so the whole is clear and the share free reads at a glance */
.crn-bar { margin-top: 8px; height: 10px; border-radius: 999px; background: rgb(var(--v-theme-light)); border: 1px solid rgb(var(--v-theme-border)); overflow: hidden; }
.crn-fill { display: block; height: 100%; border-radius: 999px; background: rgb(var(--v-theme-success)); }
.crn-bar--few .crn-fill { background: rgb(var(--v-theme-warning)); }
.crn-bar--full { border-color: rgb(var(--v-theme-error)); background: transparent; }
</style>
